import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CellContext, createColumnHelper, RowData } from '@tanstack/react-table'

import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'

import CircleRoundedIcon from '@mui/icons-material/CircleRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import AutoModeIcon from '@mui/icons-material/AutoMode'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import EditIcon from '@mui/icons-material/Edit'

import ChipButton from '@/components/ui/buttons/ChipButton'
import Table from '@/components/ui/tables/Table'

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    assignProfessionals?: (id: string) => void
    createAdmin?: (establishmentId: string) => void
    createOrdoclicEntity?: (id: string) => void
    deleteEstablishment?: (establishmentId: string) => void
    editEstablishment?: (id: string) => void
    showStats?: (establishmentId: string) => void
    showSubscriptions?: (establishmentId: string) => void
  }
}

export type EstablishmentData = {
  id: string
  active: boolean
  name: string
  ordoclicEntityId?: string
}

type EstablishmentsTableProps = {
  data: EstablishmentData[]
  onDeleteEstablishment?: (establishmentId: string) => void
  onShowStats?: (establishmentId: string) => void
  onShowSubscriptions?: (establishmentId: string) => void
  onCreateOrdoclicEntity?: (id: string) => void
  onAssignProfessionals?: (id: string) => void
  onEditEstablishment?: (id: string) => void
}

const columnHelper = createColumnHelper<EstablishmentData>()

export function EstablishmentsTable(props: EstablishmentsTableProps) {
  const { data, onAssignProfessionals, onCreateOrdoclicEntity, onDeleteEstablishment, onEditEstablishment, onShowStats, onShowSubscriptions } = props
  const { t } = useTranslation()

  const cols = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('page.superAdmin.establishmentsTable.header.name').toString(),
      }),
      columnHelper.accessor('active', {
        header: t('page.superAdmin.establishmentsTable.header.active').toString(),
        cell: ({ getValue }) => (getValue() ? <TaskAltRoundedIcon color="success" /> : <CircleRoundedIcon color="error" />),
      }),
      columnHelper.display({
        id: 'action',
        header: t('common:table.header.action').toString(),
        cell: ActionsCell,
        meta: { align: 'right' },
      }),
    ],
    []
  )

  return (
    <Table
      columns={cols}
      data={data}
      disableGlobalSearch
      meta={{
        assignProfessionals: onAssignProfessionals,
        createOrdoclicEntity: onCreateOrdoclicEntity,
        deleteEstablishment: onDeleteEstablishment,
        editEstablishment: onEditEstablishment,
        showStats: onShowStats,
        showSubscriptions: onShowSubscriptions,
      }}
    />
  )
}

function ActionsCell(props: CellContext<EstablishmentData, unknown>) {
  const {
    row,
    table: {
      options: { meta },
    },
  } = props
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleAssignProfessionals = () => {
    meta?.assignProfessionals?.(row.original.id)
  }

  const handleCreateAdmin = () => {
    meta?.createAdmin?.(row.original.id)
  }

  const handleCreateOrdoclicEntity = () => {
    meta?.createOrdoclicEntity?.(row.original.id)
  }

  const handleEditEstablishment = () => {
    meta?.editEstablishment?.(row.original.id)
  }

  const handleShowStats = () => {
    meta?.showStats?.(row.original.id)
  }

  const handleShowSubscriptions = () => {
    meta?.showSubscriptions?.(row.original.id)
  }

  return (
    <Stack justifyContent="flex-end" direction="row" spacing={1}>
      <Tooltip title={t('page.superAdmin.establishmentsTable.actions.edit')}>
        <ChipButton color="info" onClick={handleEditEstablishment} data-testid="edit-establishment">
          <EditIcon />
        </ChipButton>
      </Tooltip>
      <Tooltip title={t('common:table.header.stats')}>
        <ChipButton color="info" onClick={handleShowStats} data-testid="stats-button">
          <QueryStatsIcon />
        </ChipButton>
      </Tooltip>
      <Tooltip title={t('common:table.header.subscription')}>
        <ChipButton color="info" onClick={handleShowSubscriptions} data-testid="sub-button">
          <AutoModeIcon />
        </ChipButton>
      </Tooltip>
      <Tooltip title={t('page.superAdmin.establishmentsTable.actions.createOrdoclicEntity')}>
        <span>
          <ChipButton color="info" onClick={handleCreateOrdoclicEntity} data-testid="create-ordoclic-entity" disabled={!!row.original.ordoclicEntityId}>
            <AddCircleOutlineIcon />
          </ChipButton>
        </span>
      </Tooltip>
      <Tooltip title={t('page.superAdmin.establishmentsTable.actions.assignProfessionals')}>
        <span>
          <ChipButton color="info" onClick={handleAssignProfessionals} data-testid="assign-professionals" disabled={!row.original.ordoclicEntityId}>
            <GroupAddIcon />
          </ChipButton>
        </span>
      </Tooltip>
      <Tooltip title={t('page.superAdmin.establishmentsTable.actions.open')}>
        <ChipButton
          color="info"
          data-testid={`open-establishment-${row.original.id}`}
          onClick={() => navigate(`/super-admin/establishments/${row.original.id}`)}
        >
          <LaunchRoundedIcon />
        </ChipButton>
      </Tooltip>
    </Stack>
  )
}
